import type { SiteContent } from "../types";

/** English content. Keep structure in sync with zh.ts. */
export const en: SiteContent = {
  meta: {
    title: "DOHORIZON — Carry models across the horizon",
    description:
      "DOHORIZON is a delivery-focused AI Studio: custom conversation, fine-tuning and evaluation toolchains, plus research into new architectures. We turn AI from demos into measurable, iterable daily productivity.",
    lang: "en",
  },

  brand: "DOHORIZON",

  themeToggleLabel: "Toggle light / dark mode",

  nav: [
    { label: "News", href: "#news" },
    { label: "Models", href: "#models" },
    { label: "Services", href: "#services" },
    { label: "Research", href: "#research" },
    { label: "About", href: "#about" },
    { label: "Contact", href: "#contact" },
  ],

  hero: {
    eyebrow: "AI Studio · Models / Services / Research",
    titleLead: "Carry models across the horizon, ",
    titleAccent: "into the real world",
    lede: "We build custom conversation, fine-tuning and evaluation toolchains, and keep researching new architectures — so that AGI can accompany humanity.",
    ctas: [
      { label: "Explore capabilities →", href: "#services", variant: "solid" },
      { label: "Book a project chat", href: "#contact", variant: "ghost" },
    ],
  },

  news: {
    id: "news",
    header: {
      eyebrow: "News",
      title: "Latest updates",
      intro: "Product releases, research progress and company news.",
    },
    items: [
      {
        category: "Release",
        title: "DoHorizon-V1 released: tool use in conversation and roleplay",
        date: "2026-06",
        href: "#",
        featured: true,
      },
    ],
    moreLabel: "View all updates →",
    moreHref: "#",
  },

  capabilities: {
    id: "services",
    header: {
      eyebrow: "Services",
      title: "What we deliver",
      intro: "From conversation systems to training and evaluation — the full path to shipping AI.",
    },
    items: [
      { title: "Custom conversation", description: "Controllable, auditable chat systems built around your knowledge base and tone." },
      { title: "Fine-tuning & eval", description: "End-to-end training, evaluation and deployment pipelines — every step backed by numbers." },
      { title: "Architecture research", description: "Small models and multi-agent collaboration for lighter, steadier deployments." },
    ],
  },

  models: {
    id: "models",
    header: {
      eyebrow: "Models",
      title: "The DoHorizon model family",
      intro: "Our first model — multimodal understanding paired with empathetic, lifelike roleplay.",
    },
    labels: { params: "Params", context: "Context", featured: "Featured" },
    items: [
      {
        name: "DoHorizon-V1",
        tagline: "Multimodal · Roleplay",
        params: "27B",
        context: "128K",
        tags: ["Multimodal", "Roleplay", "Tool use"],
        description: "27B parameters, 128K context — covering multimodal understanding and empathetic, lifelike roleplay.",
        featured: true,
      },
    ],
  },

  story: {
    id: "story",
    kicker: "Feature Story · Made with you",
    title: "Every deployment ships with measurable evidence",
    lede: "The full journey of a real customer case — from assessment through fine-tuning to production — and the numbers behind each stage.",
    cta: { label: "Read the full story →", href: "#", variant: "solid" },
  },

  research: {
    id: "research",
    header: {
      eyebrow: "Research",
      title: "What we're researching",
      intro: "Turning frontier exploration into reusable engineering.",
    },
    items: [
      { area: "Architecture", title: "Efficient small models", summary: "Approaching large-model quality at a fraction of the parameters, lowering the bar to deploy.", status: "Ongoing" },
      { area: "Agents", title: "Multi-agent collaboration", summary: "Specialized agents dividing labor to complete long-horizon tasks reliably.", status: "Ongoing" },
      { area: "Evaluation", title: "Measurable alignment eval", summary: "Business-facing eval sets and metrics that make quality quantifiable and regression-safe.", status: "Published" },
      { area: "Inference", title: "Low-latency inference", summary: "Operator fusion and quantization to raise throughput and cut cost on the same hardware.", status: "Ongoing" },
    ],
  },

  values: {
    id: "values",
    quoteLead: "AI's value isn't in demos, ",
    quoteAccent: "but in daily productivity",
    pillars: [
      { title: "Measurable", description: "Every delivery comes with numbers — quantifiable and regression-safe." },
      { title: "Auditable", description: "System behavior is controllable and traceable; trust built on transparency." },
      { title: "Iterable", description: "Designed for continuous evolution from day one, not one-off delivery." },
    ],
  },

  about: {
    id: "about",
    header: {
      eyebrow: "About",
      title: "Spanning research and business",
    },
    paragraphs: [
      "DOHORIZON is a delivery-focused AI Studio. We do frontier model and architecture research, and polish the results into products and services enterprises can actually use.",
      "We believe AI's value isn't in demos but in measurable, iterable daily productivity — so every delivery comes with numbers.",
    ],
    stats: [
      { value: "3+", label: "In-house model specs" },
      { value: "20+", label: "Shipped projects" },
      { value: "99.9%", label: "Service uptime" },
      { value: "<200ms", label: "On-device latency" },
    ],
  },

  milestones: {
    id: "milestones",
    header: {
      eyebrow: "Milestones",
      title: "Milestones so far",
      intro: "Key moments from the first line of code to deployment at scale.",
    },
    items: [
      { year: "2025", title: "DOHORIZON founded", description: "Started as a small studio with a research-to-business roadmap." },
      { year: "2026", title: "The DoHorizon family debuts", description: "DoHorizon-V1 released — multimodal understanding with empathetic, lifelike roleplay." },
    ],
  },

  team: {
    id: "team",
    header: {
      eyebrow: "Team",
      title: "Four domains, one goal",
      intro: "An interdisciplinary team walks research the last mile into production.",
    },
    domains: [
      { name: "Research", description: "Frontier work on architectures, alignment and evaluation methods." },
      { name: "Engineering", description: "Training and inference infrastructure — reproducible and scalable." },
      { name: "Product", description: "Packaging capability into usable, measurable customer experiences." },
      { name: "Operations", description: "Delivery, customer success and the systems that keep us running." },
    ],
  },

  careers: {
    id: "careers",
    title: "Carry models across the horizon with us",
    intro: "We're looking for researchers, engineers and product partners — whatever your time zone.",
    cta: { label: "Join us (TBD) →", href: "/coming-soon.html", variant: "solid" },
  },

  newsletter: {
    id: "newsletter",
    title: "Subscribe to the DOHORIZON letter",
    intro: "One email a month — model updates, research notes and case studies.",
    placeholder: "your@email.com",
    submit: "Subscribe",
    success: "Subscribed — welcome aboard.",
    invalid: "Please enter a valid email.",
  },

  contact: {
    id: "contact",
    header: {
      eyebrow: "Contact",
      title: "Let's talk about your use case",
      intro: "Custom conversation, fine-tuning, or just a technical assessment — we're happy to chat.",
    },
    form: {
      name: "Name",
      email: "Email",
      message: "What would you like to discuss?",
      submit: "Send request",
      success: "Got it — we'll be in touch shortly.",
      invalid: "Please fill in your name, a valid email and a message.",
    },
    info: { email: "contact@dohorizon.com", location: "China · Remote-friendly" },
  },

  footer: {
    copyright: `© ${new Date().getFullYear()} DOHORIZON`,
    tagline: "Research → Business",
    columns: [
      {
        title: "Products & Services",
        links: [
          { label: "Models", href: "#models" },
          { label: "Services", href: "#services" },
          { label: "News", href: "#news" },
        ],
      },
      {
        title: "Research & Technology",
        links: [
          { label: "Research areas", href: "#research" },
          { label: "Feature story", href: "#story" },
          { label: "Tech blog", href: "#" },
        ],
      },
      {
        title: "About us",
        links: [
          { label: "Company", href: "#about" },
          { label: "Values", href: "#values" },
          { label: "Join us (TBD)", href: "/coming-soon.html" },
          { label: "Contact", href: "#contact" },
        ],
      },
    ],
    legal: [
      { label: "Privacy", href: "#" },
      { label: "Terms", href: "#" },
    ],
  },
};
